import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiSearch, 
  FiEye, 
  FiBookOpen,
  FiX,
  FiTrash2,
  FiArrowLeft,
  FiPlus,
  FiSave
} from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useDaumPostcode } from '../../hooks/useDaumPostcode';
import {
  formatBillingDate
} from '../../utils/franchiseBilling';

const INITIAL_FRANCHISE_FORM = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  phone: '',
  address: '',
  addressDetail: ''
};

const INITIAL_FRANCHISE_EDIT_FORM = {
  name: '',
  email: '',
  phone: '',
  address: '',
  addressDetail: '',
  password: '',
  confirmPassword: ''
};

const buildFranchiseEditForm = (user) => ({
  name: user?.name || '',
  email: user?.email || '',
  phone: user?.phone || '',
  address: user?.address || '',
  addressDetail: '',
  password: '',
  confirmPassword: ''
});

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const { ready: postcodeReady, openPostcode } = useDaumPostcode();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showFranchiseModal, setShowFranchiseModal] = useState(false);
  const [franchiseForm, setFranchiseForm] = useState(INITIAL_FRANCHISE_FORM);
  const [isSubmittingFranchise, setIsSubmittingFranchise] = useState(false);
  const [emailCheckStatus, setEmailCheckStatus] = useState(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [franchiseCourses, setFranchiseCourses] = useState([]);
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState([]);
  const [purchasedCourseIds, setPurchasedCourseIds] = useState([]);
  const [loadingAssignment, setLoadingAssignment] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [billingMonthlyFee, setBillingMonthlyFee] = useState(0);
  const [billingStartDate, setBillingStartDate] = useState('');
  const [billingPayments, setBillingPayments] = useState([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [franchiseEditForm, setFranchiseEditForm] = useState(INITIAL_FRANCHISE_EDIT_FORM);
  const [franchiseEditEmailStatus, setFranchiseEditEmailStatus] = useState(null);
  const [isCheckingFranchiseEditEmail, setIsCheckingFranchiseEditEmail] = useState(false);
  const [savingFranchiseProfile, setSavingFranchiseProfile] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterRole]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        search: debouncedSearch,
        role: filterRole
      });
      
      const response = await axios.get(`/api/admin/users?${params}`);
      setUsers(response.data.users || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalUsers(response.data.totalUsers || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('사용자 목록을 불러오는데 실패했습니다');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, filterRole]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    setFranchiseEditForm(buildFranchiseEditForm(user));
    setFranchiseEditEmailStatus(null);

    if (user.isFranchise) {
      await Promise.all([
        fetchFranchiseCourses(user._id),
        fetchFranchiseBilling(user._id)
      ]);
    } else {
      setFranchiseCourses([]);
      setSelectedAssignmentIds([]);
      setPurchasedCourseIds([]);
      setBillingPayments([]);
      setBillingMonthlyFee(0);
      setBillingStartDate('');
    }
  };

  const fetchFranchiseBilling = async (userId) => {
    try {
      setLoadingBilling(true);
      const response = await axios.get(`/api/admin/users/${userId}/franchise-billing`);
      setBillingMonthlyFee(response.data.monthlyFee || 0);
      setBillingStartDate(response.data.billingStartDate ? response.data.billingStartDate.split('T')[0] : '');
      setBillingPayments(response.data.payments || []);
    } catch (error) {
      console.error('Error fetching franchise billing:', error);
      toast.error('가맹점 납부 정보를 불러오는데 실패했습니다');
      setBillingPayments([]);
    } finally {
      setLoadingBilling(false);
    }
  };

  const handleBillingPaymentChange = (period, field, value) => {
    setBillingPayments((prev) => prev.map((payment) => (
      payment.period === period ? { ...payment, [field]: value } : payment
    )));
  };

  const handleSaveFranchiseBilling = async () => {
    if (!selectedUser?.isFranchise) return;

    setSavingBilling(true);
    try {
      const response = await axios.put(`/api/admin/users/${selectedUser._id}/franchise-billing`, {
        monthlyFee: Number(billingMonthlyFee) || 0,
        billingStartDate: billingStartDate || undefined,
        records: billingPayments.map((payment) => ({
          period: payment.period,
          amount: Number(payment.amount) || 0,
          status: payment.status,
          note: payment.note || '',
          dueDate: payment.dueDate,
          paidAt: payment.paidAt
        }))
      });
      setBillingMonthlyFee(response.data.monthlyFee || 0);
      setBillingStartDate(response.data.billingStartDate ? response.data.billingStartDate.split('T')[0] : '');
      setBillingPayments(response.data.payments || []);
      toast.success('가맹점 납부 정보가 저장되었습니다');
    } catch (error) {
      console.error('Error saving franchise billing:', error);
      toast.error(error.response?.data?.message || '가맹점 납부 정보 저장에 실패했습니다');
    } finally {
      setSavingBilling(false);
    }
  };

  const fetchFranchiseCourses = async (userId) => {
    try {
      setLoadingAssignment(true);
      const response = await axios.get(`/api/admin/users/${userId}/franchise-courses`);
      setFranchiseCourses(response.data.courses || []);
      setSelectedAssignmentIds(response.data.assignedCourseIds || []);
      setPurchasedCourseIds(response.data.purchasedCourseIds || []);
    } catch (error) {
      console.error('Error fetching franchise courses:', error);
      toast.error('강의 배정 정보를 불러오는데 실패했습니다');
      setFranchiseCourses([]);
      setSelectedAssignmentIds([]);
      setPurchasedCourseIds([]);
    } finally {
      setLoadingAssignment(false);
    }
  };

  const handleToggleAssignmentCourse = (courseId) => {
    setSelectedAssignmentIds((prev) => (
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId]
    ));
  };

  const handleToggleSelectAllCourses = () => {
    if (selectedAssignmentIds.length === franchiseCourses.length) {
      setSelectedAssignmentIds([]);
    } else {
      setSelectedAssignmentIds(franchiseCourses.map((course) => course._id));
    }
  };

  const handleSaveFranchiseCourses = async () => {
    if (!selectedUser?.isFranchise) return;

    setSavingAssignment(true);
    try {
      const response = await axios.put(
        `/api/admin/users/${selectedUser._id}/franchise-courses`,
        { courseIds: selectedAssignmentIds }
      );
      setSelectedUser(response.data.user);
      setSelectedAssignmentIds(response.data.assignedCourseIds || []);
      fetchUsers();
      toast.success('가맹점 강의 배정이 저장되었습니다');
    } catch (error) {
      console.error('Error saving franchise courses:', error);
      toast.error(error.response?.data?.message || '강의 배정 저장에 실패했습니다');
    } finally {
      setSavingAssignment(false);
    }
  };

  const getEnrollmentSourceBadge = (enrollment) => {
    if (enrollment.source === 'franchise') {
      return (
        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-200">
          CRM 배정
        </span>
      );
    }

    return (
      <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
        결제
      </span>
    );
  };

  const handleFranchiseFormChange = (e) => {
    const { name, value } = e.target;
    setFranchiseForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'email') {
      setEmailCheckStatus(null);
    }
  };

  const resetFranchiseForm = () => {
    setFranchiseForm(INITIAL_FRANCHISE_FORM);
    setEmailCheckStatus(null);
  };

  const handleCheckEmail = async () => {
    const email = franchiseForm.email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      toast.error('이메일을 입력해주세요');
      return;
    }

    if (!emailRegex.test(email)) {
      setEmailCheckStatus('invalid');
      toast.error('유효한 이메일 형식이 아닙니다');
      return;
    }

    setIsCheckingEmail(true);
    try {
      const response = await axios.get(`/api/admin/users/check-email?email=${encodeURIComponent(email)}`);
      setEmailCheckStatus(response.data.available ? 'available' : 'duplicate');
      if (response.data.available) {
        toast.success('사용 가능한 이메일입니다');
      } else {
        toast.error('이미 사용 중인 이메일입니다');
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailCheckStatus('invalid');
      toast.error(error.response?.data?.message || '이메일 확인에 실패했습니다');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleOpenAddressSearch = () => {
    if (!postcodeReady) {
      toast.error('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const opened = openPostcode(({ address }) => {
      setFranchiseForm((prev) => ({ ...prev, address }));
    });

    if (!opened) {
      toast.error('주소 검색을 열 수 없습니다');
    }
  };

  const handleCreateFranchise = async (e) => {
    e.preventDefault();

    if (emailCheckStatus !== 'available') {
      toast.error('이메일 중복 확인을 완료해주세요');
      return;
    }

    if (!franchiseForm.address.trim()) {
      toast.error('주소 검색을 통해 기본 주소를 입력해주세요');
      return;
    }

    if (franchiseForm.password !== franchiseForm.confirmPassword) {
      toast.error('비밀번호가 일치하지 않습니다');
      return;
    }

    setIsSubmittingFranchise(true);
    try {
      const { confirmPassword, addressDetail, ...rest } = franchiseForm;
      const fullAddress = [rest.address, addressDetail.trim()].filter(Boolean).join(' ').trim();

      await axios.post('/api/admin/users/franchise', {
        ...rest,
        address: fullAddress
      });
      toast.success('가맹점 계정이 등록되었습니다');
      setShowFranchiseModal(false);
      resetFranchiseForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating franchise account:', error);
      const message = error.response?.data?.message
        || error.response?.data?.errors?.[0]?.msg
        || '가맹점 계정 등록에 실패했습니다';
      toast.error(message);
    } finally {
      setIsSubmittingFranchise(false);
    }
  };

  const handleFranchiseEditFormChange = (e) => {
    const { name, value } = e.target;
    setFranchiseEditForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'email') {
      setFranchiseEditEmailStatus(null);
    }
  };

  const handleCheckFranchiseEditEmail = async () => {
    if (!selectedUser) return;

    const email = franchiseEditForm.email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      toast.error('이메일을 입력해주세요');
      return;
    }

    if (!emailRegex.test(email)) {
      setFranchiseEditEmailStatus('invalid');
      toast.error('유효한 이메일 형식이 아닙니다');
      return;
    }

    if (email.toLowerCase() === selectedUser.email?.toLowerCase()) {
      setFranchiseEditEmailStatus('available');
      toast.success('현재 사용 중인 이메일입니다');
      return;
    }

    setIsCheckingFranchiseEditEmail(true);
    try {
      const response = await axios.get(
        `/api/admin/users/check-email?email=${encodeURIComponent(email)}&excludeUserId=${selectedUser._id}`
      );
      setFranchiseEditEmailStatus(response.data.available ? 'available' : 'duplicate');
      if (response.data.available) {
        toast.success('사용 가능한 이메일입니다');
      } else {
        toast.error('이미 사용 중인 이메일입니다');
      }
    } catch (error) {
      console.error('Error checking franchise edit email:', error);
      setFranchiseEditEmailStatus('invalid');
      toast.error(error.response?.data?.message || '이메일 확인에 실패했습니다');
    } finally {
      setIsCheckingFranchiseEditEmail(false);
    }
  };

  const handleOpenFranchiseEditAddressSearch = () => {
    if (!postcodeReady) {
      toast.error('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const opened = openPostcode(({ address }) => {
      setFranchiseEditForm((prev) => ({ ...prev, address }));
    });

    if (!opened) {
      toast.error('주소 검색을 열 수 없습니다');
    }
  };

  const handleSaveFranchiseProfile = async (e) => {
    e.preventDefault();
    if (!selectedUser?.isFranchise) return;

    const emailUnchanged = franchiseEditForm.email.trim().toLowerCase() === selectedUser.email?.toLowerCase();
    if (!emailUnchanged && franchiseEditEmailStatus !== 'available') {
      toast.error('이메일 중복 확인을 완료해주세요');
      return;
    }

    if (!franchiseEditForm.address.trim()) {
      toast.error('주소를 입력해주세요');
      return;
    }

    if (franchiseEditForm.password || franchiseEditForm.confirmPassword) {
      if (franchiseEditForm.password !== franchiseEditForm.confirmPassword) {
        toast.error('비밀번호가 일치하지 않습니다');
        return;
      }
      if (franchiseEditForm.password.length < 6) {
        toast.error('비밀번호는 최소 6자 이상이어야 합니다');
        return;
      }
    }

    setSavingFranchiseProfile(true);
    try {
      const { confirmPassword, addressDetail, password, ...rest } = franchiseEditForm;
      const fullAddress = [rest.address, addressDetail.trim()].filter(Boolean).join(' ').trim();

      const payload = {
        name: rest.name,
        email: rest.email,
        phone: rest.phone,
        address: fullAddress
      };

      if (password) {
        payload.password = password;
      }

      const response = await axios.put(`/api/admin/users/${selectedUser._id}/franchise`, payload);
      const updatedUser = response.data.user;

      setSelectedUser(updatedUser);
      setFranchiseEditForm(buildFranchiseEditForm(updatedUser));
      setFranchiseEditEmailStatus(null);
      setUsers((prev) => prev.map((user) => (user._id === updatedUser._id ? { ...user, ...updatedUser } : user)));
      toast.success('가맹점 계정 정보가 저장되었습니다');
    } catch (error) {
      console.error('Error updating franchise profile:', error);
      const message = error.response?.data?.message
        || error.response?.data?.errors?.[0]?.msg
        || '가맹점 계정 정보 저장에 실패했습니다';
      toast.error(message);
    } finally {
      setSavingFranchiseProfile(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`"${userName}" 사용자를 정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }

    try {
      await axios.delete(`/api/admin/users/${userId}`);
      toast.success('사용자가 삭제되었습니다');
      if (selectedUser?._id === userId) {
        setShowUserModal(false);
        setSelectedUser(null);
      }
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || '사용자 삭제에 실패했습니다');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatGender = (gender) => {
    const labels = { male: '남성', female: '여성', other: '기타' };
    return labels[gender] || gender || '-';
  };

  const getEnrolledCoursesCount = (user) => {
    return user.enrolledCourses?.length || 0;
  };

  const getTotalProgress = (user) => {
    if (!user.enrolledCourses || user.enrolledCourses.length === 0) return 0;
    
    const totalProgress = user.enrolledCourses.reduce((sum, enrollment) => {
      return sum + (enrollment.progress || 0);
    }, 0);
    
    return Math.round(totalProgress / user.enrolledCourses.length);
  };

  const getCourseProgress = (enrollment) => {
    return enrollment.progress || 0;
  };

  const getCourseStatus = (progress) => {
    if (progress === 0) return { text: '시작 안함', color: 'bg-gray-100 text-gray-800' };
    if (progress < 25) return { text: '초급', color: 'bg-blue-100 text-blue-800' };
    if (progress < 50) return { text: '진행 중', color: 'bg-yellow-100 text-yellow-800' };
    if (progress < 75) return { text: '고급', color: 'bg-orange-100 text-orange-800' };
    if (progress < 100) return { text: '거의 완료', color: 'bg-green-100 text-green-800' };
    return { text: '완료', color: 'bg-green-100 text-green-800' };
  };

  const isCurrentUser = (userId) => currentUser?._id === userId;

  const renderAccountTypeBadge = (user) => {
    if (user.isFranchise) {
      return (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-200">
          가맹점 계정
        </span>
      );
    }

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
        user.role === 'admin'
          ? 'bg-purple-100 text-purple-800'
          : 'bg-blue-100 text-blue-800'
      }`}>
        {user.role === 'admin' ? '관리자' : '학생'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link
            to="/admin-dashboard"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <FiArrowLeft className="w-4 h-4 mr-1" />
            관리자 대시보드로 돌아가기
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">사용자 관리</h1>
              <p className="mt-2 text-gray-600">
                등록된 모든 사용자를 확인하고 관리하세요 (총 {totalUsers}명)
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowFranchiseModal(true)}
              className="btn-primary inline-flex items-center justify-center shrink-0"
            >
              <FiPlus className="w-4 h-4 mr-2" />
              가맹점 계정 등록
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="이름, 이메일, 전화번호로 검색..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="md:w-48">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">모든 역할</option>
                <option value="student">학생</option>
                <option value="franchise">가맹점</option>
                <option value="admin">관리자</option>
              </select>
            </div>
          </div>
        </div>

        {users.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FiSearch className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">사용자를 찾을 수 없습니다</h3>
            <p className="text-gray-500">
              {debouncedSearch || filterRole !== 'all'
                ? '검색 조건에 맞는 사용자가 없습니다.'
                : '등록된 사용자가 없습니다.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {users.map((user) => (
              <div key={user._id} className={`bg-white rounded-lg shadow-sm border ${
                user.isFranchise ? 'border-amber-200 border-l-4 border-l-amber-400' : 'border-gray-200'
              }`}>
                <div className="p-6 border-b border-gray-200">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-center space-x-4">
                      <img
                        src={user.avatar || '/images/default-avatar.svg'}
                        alt={user.name}
                        className="w-12 h-12 rounded-full"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">{user.name}</h3>
                          {isCurrentUser(user._id) && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">나</span>
                          )}
                        </div>
                        <p className="text-gray-600">{user.email}</p>
                        <p className="text-sm text-gray-500">{user.phone}</p>
                        <div className="flex items-center flex-wrap gap-2 mt-1">
                          {renderAccountTypeBadge(user)}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.isVerified
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {user.isVerified ? '인증 완료' : '미인증'}
                          </span>
                          <span className="text-sm text-gray-500">
                            가입일: {formatDate(user.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center flex-wrap gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{getEnrolledCoursesCount(user)}</div>
                        <div className="text-sm text-gray-500">수강 강의</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{getTotalProgress(user)}%</div>
                        <div className="text-sm text-gray-500">평균 진행률</div>
                      </div>
                      <button
                        onClick={() => handleViewUser(user)}
                        className="btn-primary flex items-center"
                      >
                        <FiEye className="w-4 h-4 mr-2" />
                        상세 보기
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user._id, user.name)}
                        disabled={isCurrentUser(user._id)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        title="사용자 삭제"
                      >
                        <FiTrash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {user.enrolledCourses && user.enrolledCourses.length > 0 && (
                  <div className="p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">수강 중인 강의</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {user.enrolledCourses.map((enrollment, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900 mb-1">
                                {enrollment.course?.title || '알 수 없는 강의'}
                              </h5>
                              <p className="text-sm text-gray-600 mb-2">
                                {enrollment.course?.subtitle || '설명 없음'}
                              </p>
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <span>{enrollment.course?.category || '-'}</span>
                                <span>•</span>
                                <span>{enrollment.course?.level || '-'}</span>
                                <span>•</span>
                                <span>₩{enrollment.course?.price?.toLocaleString() || '0'}</span>
                              </div>
                            </div>
                            <img
                              src={enrollment.course?.thumbnail || '/images/default-course.svg'}
                              alt={enrollment.course?.title}
                              className="w-12 h-12 rounded-lg object-cover ml-3"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">진행률</span>
                              <span className="text-sm font-medium">{getCourseProgress(enrollment)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${getCourseProgress(enrollment)}%` }}
                              ></div>
                            </div>
                            
                            <div className="flex items-center justify-between text-sm">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                getCourseStatus(getCourseProgress(enrollment)).color
                              }`}>
                                {getCourseStatus(getCourseProgress(enrollment)).text}
                              </span>
                              <span className="text-gray-500">
                                {enrollment.enrolledAt ? formatDate(enrollment.enrolledAt) : '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!user.enrolledCourses || user.enrolledCourses.length === 0) && (
                  <div className="p-6 text-center">
                    <FiBookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">수강 중인 강의가 없습니다</h4>
                    <p className="text-gray-500">이 사용자는 아직 어떤 강의에도 등록하지 않았습니다.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              총 {totalUsers}명 중 {(currentPage - 1) * 10 + 1}–{Math.min(currentPage * 10, totalUsers)}명 표시
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
          </div>
        )}

        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="w-[90vw] h-[90vh] bg-white rounded-lg shadow-lg border flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                <h3 className="text-2xl font-bold text-gray-900">사용자 상세 정보</h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-1">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <div className="text-center mb-6">
                        <img
                          src={selectedUser.avatar || '/images/default-avatar.svg'}
                          alt={selectedUser.name}
                          className="w-24 h-24 rounded-full mx-auto mb-4"
                        />
                        <h4 className="text-xl font-semibold text-gray-900">{selectedUser.name}</h4>
                        <p className="text-gray-600">{selectedUser.email}</p>
                        <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                          {renderAccountTypeBadge(selectedUser)}
                        </div>
                      </div>

                      {selectedUser.isFranchise ? (
                        <form onSubmit={handleSaveFranchiseProfile} className="space-y-4">
                          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 text-center">
                            가맹점 계정 정보를 수정할 수 있습니다
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">가맹점명 / 담당자명</label>
                            <input
                              type="text"
                              name="name"
                              value={franchiseEditForm.name}
                              onChange={handleFranchiseEditFormChange}
                              required
                              minLength={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                            <div className="flex gap-2">
                              <input
                                type="email"
                                name="email"
                                value={franchiseEditForm.email}
                                onChange={handleFranchiseEditFormChange}
                                required
                                className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                  franchiseEditEmailStatus === 'available'
                                    ? 'border-green-400'
                                    : franchiseEditEmailStatus === 'duplicate' || franchiseEditEmailStatus === 'invalid'
                                      ? 'border-red-400'
                                      : 'border-gray-300'
                                }`}
                              />
                              <button
                                type="button"
                                onClick={handleCheckFranchiseEditEmail}
                                disabled={isCheckingFranchiseEditEmail || !franchiseEditForm.email.trim()}
                                className="whitespace-nowrap px-3 py-2 border border-primary-600 text-sm font-medium rounded-lg text-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isCheckingFranchiseEditEmail ? '확인 중...' : '중복 확인'}
                              </button>
                            </div>
                            {franchiseEditEmailStatus === 'available' && (
                              <p className="mt-1 text-sm text-green-600">사용 가능한 이메일입니다</p>
                            )}
                            {franchiseEditEmailStatus === 'duplicate' && (
                              <p className="mt-1 text-sm text-red-600">이미 사용 중인 이메일입니다</p>
                            )}
                            {franchiseEditEmailStatus === 'invalid' && (
                              <p className="mt-1 text-sm text-red-600">유효한 이메일 형식이 아닙니다</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">전화번호</label>
                            <input
                              type="tel"
                              name="phone"
                              value={franchiseEditForm.phone}
                              onChange={handleFranchiseEditFormChange}
                              required
                              pattern="^01[0-9]{1}-?[0-9]{4}-?[0-9]{4}$"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="010-1234-5678"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
                            <div className="flex gap-2 mb-2">
                              <input
                                type="text"
                                name="address"
                                value={franchiseEditForm.address}
                                readOnly
                                required
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none"
                                placeholder="주소 검색 버튼을 눌러주세요"
                              />
                              <button
                                type="button"
                                onClick={handleOpenFranchiseEditAddressSearch}
                                disabled={!postcodeReady}
                                className="whitespace-nowrap px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {postcodeReady ? '주소 검색' : '로딩 중...'}
                              </button>
                            </div>
                            <input
                              type="text"
                              name="addressDetail"
                              value={franchiseEditForm.addressDetail}
                              onChange={handleFranchiseEditFormChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="상세 주소 (동/호수 등)"
                            />
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호</label>
                              <input
                                type="password"
                                name="password"
                                value={franchiseEditForm.password}
                                onChange={handleFranchiseEditFormChange}
                                minLength={6}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="변경하지 않으면 비워두세요"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호 확인</label>
                              <input
                                type="password"
                                name="confirmPassword"
                                value={franchiseEditForm.confirmPassword}
                                onChange={handleFranchiseEditFormChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="비밀번호 변경 시 입력"
                              />
                            </div>
                          </div>

                          <div className="space-y-2 pt-2 border-t border-gray-200 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">가입일</span>
                              <span className="font-medium">{formatDate(selectedUser.createdAt)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">수강 강의</span>
                              <span className="font-medium">{getEnrolledCoursesCount(selectedUser)}개</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">평균 진행률</span>
                              <span className="font-medium">{getTotalProgress(selectedUser)}%</span>
                            </div>
                          </div>

                          <button
                            type="submit"
                            disabled={savingFranchiseProfile}
                            className="w-full btn-primary inline-flex items-center justify-center disabled:opacity-50"
                          >
                            <FiSave className="w-4 h-4 mr-2" />
                            {savingFranchiseProfile ? '저장 중...' : '계정 정보 저장'}
                          </button>
                        </form>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">전화번호:</span>
                            <span className="font-medium">{selectedUser.phone || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">성별:</span>
                            <span className="font-medium">{selectedUser.gender ? formatGender(selectedUser.gender) : '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">주소:</span>
                            <span className="font-medium text-right max-w-[60%]">{selectedUser.address || '-'}</span>
                          </div>
                          {selectedUser.dateOfBirth && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">생년월일:</span>
                              <span className="font-medium">
                                {new Date(selectedUser.dateOfBirth).toLocaleDateString('ko-KR')}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-600">인증 상태:</span>
                            <span className="font-medium">{selectedUser.isVerified ? '인증 완료' : '미인증'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">가입일:</span>
                            <span className="font-medium">{formatDate(selectedUser.createdAt)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">수강 강의:</span>
                            <span className="font-medium">{getEnrolledCoursesCount(selectedUser)}개</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">평균 진행률:</span>
                            <span className="font-medium">{getTotalProgress(selectedUser)}%</span>
                          </div>
                        </div>
                      )}

                      {!isCurrentUser(selectedUser._id) && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => handleDeleteUser(selectedUser._id, selectedUser.name)}
                            className="w-full flex items-center justify-center px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <FiTrash2 className="w-4 h-4 mr-2" />
                            사용자 삭제
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="lg:col-span-2 space-y-6">
                    {selectedUser.isFranchise && (
                      <div className="border border-amber-200 rounded-lg p-5 bg-amber-50/40">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                          <div>
                            <h5 className="text-lg font-semibold text-gray-900">배정 강의 관리</h5>
                            <p className="text-sm text-gray-600 mt-1">
                              CRM에서 배정한 강의는 결제 없이 수강 가능합니다. 배정되지 않은 강의는 일반 사용자처럼 별도 결제가 필요합니다.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleSaveFranchiseCourses}
                            disabled={savingAssignment || loadingAssignment}
                            className="btn-primary inline-flex items-center justify-center shrink-0 disabled:opacity-50"
                          >
                            <FiSave className="w-4 h-4 mr-2" />
                            {savingAssignment ? '저장 중...' : '배정 저장'}
                          </button>
                        </div>

                        {loadingAssignment ? (
                          <div className="py-8 text-center text-gray-500">강의 목록을 불러오는 중...</div>
                        ) : franchiseCourses.length === 0 ? (
                          <div className="py-8 text-center text-gray-500">등록된 강의가 없습니다</div>
                        ) : (
                          <>
                            <label className="flex items-center gap-3 p-3 mb-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="checkbox"
                                checked={selectedAssignmentIds.length === franchiseCourses.length && franchiseCourses.length > 0}
                                onChange={handleToggleSelectAllCourses}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                              <span className="font-medium text-gray-900">전체 선택 ({franchiseCourses.length}개)</span>
                            </label>

                            <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                              {franchiseCourses.map((course) => {
                                const isAssigned = selectedAssignmentIds.includes(course._id);
                                const isPurchased = purchasedCourseIds.includes(course._id);

                                return (
                                  <label
                                    key={course._id}
                                    className={`flex items-start gap-3 p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50 ${
                                      isAssigned ? 'border-amber-300' : 'border-gray-200'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isAssigned}
                                      onChange={() => handleToggleAssignmentCourse(course._id)}
                                      className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                    />
                                    <img
                                      src={course.thumbnail || '/images/default-course.svg'}
                                      alt={course.title}
                                      className="w-12 h-12 rounded-lg object-cover shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-gray-900">{course.title}</span>
                                        {!course.isPublished && (
                                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">비공개</span>
                                        )}
                                        {isPurchased && (
                                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">별도 결제 완료</span>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-600 truncate">{course.subtitle || '설명 없음'}</p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {course.category || '-'} · {course.level || '-'} · ₩{course.price?.toLocaleString() || '0'}
                                      </p>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>

                            <p className="text-sm text-gray-600 mt-3">
                              현재 {selectedAssignmentIds.length}개 강의 배정됨
                            </p>
                          </>
                        )}
                      </div>
                    )}

                    {selectedUser.isFranchise && (
                      <div className="border border-blue-200 rounded-lg p-5 bg-blue-50/40">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                          <div>
                            <h5 className="text-lg font-semibold text-gray-900">가맹점 납부 관리</h5>
                            <p className="text-sm text-gray-600 mt-1">
                              월 납부액과 각 월별 납부 상태를 CRM에서 직접 관리합니다.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleSaveFranchiseBilling}
                            disabled={savingBilling || loadingBilling}
                            className="btn-primary inline-flex items-center justify-center shrink-0 disabled:opacity-50"
                          >
                            <FiSave className="w-4 h-4 mr-2" />
                            {savingBilling ? '저장 중...' : '납부 정보 저장'}
                          </button>
                        </div>

                        {loadingBilling ? (
                          <div className="py-8 text-center text-gray-500">납부 정보를 불러오는 중...</div>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">월 납부액 (원)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={billingMonthlyFee}
                                  onChange={(e) => setBillingMonthlyFee(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">가맹점 등록일 (청구 시작일)</label>
                                <input
                                  type="date"
                                  value={billingStartDate}
                                  onChange={(e) => setBillingStartDate(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                            </div>

                            <div className="overflow-x-auto bg-white border rounded-lg max-h-80 overflow-y-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">청구월</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">납부액</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">납부 기한</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">상태</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">비고</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {billingPayments.length === 0 ? (
                                    <tr>
                                      <td colSpan="5" className="px-3 py-6 text-center text-gray-500">
                                        청구 내역이 없습니다.
                                      </td>
                                    </tr>
                                  ) : (
                                    [...billingPayments].reverse().map((payment) => (
                                      <tr key={payment.period}>
                                        <td className="px-3 py-2 text-sm font-medium text-gray-900">{payment.label}</td>
                                        <td className="px-3 py-2">
                                          <input
                                            type="number"
                                            min="0"
                                            value={payment.amount}
                                            onChange={(e) => handleBillingPaymentChange(payment.period, 'amount', e.target.value)}
                                            className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
                                          />
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-600">{formatBillingDate(payment.dueDate)}</td>
                                        <td className="px-3 py-2">
                                          <select
                                            value={payment.status}
                                            onChange={(e) => handleBillingPaymentChange(payment.period, 'status', e.target.value)}
                                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                                          >
                                            <option value="pending">납부 대기</option>
                                            <option value="paid">납부 완료</option>
                                            <option value="overdue">연체</option>
                                            <option value="waived">면제</option>
                                          </select>
                                        </td>
                                        <td className="px-3 py-2">
                                          <input
                                            type="text"
                                            value={payment.note || ''}
                                            onChange={(e) => handleBillingPaymentChange(payment.period, 'note', e.target.value)}
                                            className="w-full min-w-[120px] px-2 py-1 border border-gray-300 rounded text-sm"
                                            placeholder="비고"
                                          />
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div>
                    <h5 className="text-lg font-semibold text-gray-900 mb-4">수강 중인 강의</h5>
                    {selectedUser.enrolledCourses && selectedUser.enrolledCourses.length > 0 ? (
                      <div className="space-y-4">
                        {selectedUser.enrolledCourses.map((enrollment, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start space-x-4">
                              <img
                                src={enrollment.course?.thumbnail || '/images/default-course.svg'}
                                alt={enrollment.course?.title}
                                className="w-16 h-16 rounded-lg object-cover"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <h6 className="font-medium text-gray-900">
                                    {enrollment.course?.title || '알 수 없는 강의'}
                                  </h6>
                                  {getEnrollmentSourceBadge(enrollment)}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">
                                  {enrollment.course?.subtitle || '설명 없음'}
                                </p>
                                <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                                  <span>{enrollment.course?.category || '-'}</span>
                                  <span>•</span>
                                  <span>{enrollment.course?.level || '-'}</span>
                                  <span>•</span>
                                  <span>₩{enrollment.course?.price?.toLocaleString() || '0'}</span>
                                </div>
                                
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">진행률</span>
                                    <span className="text-sm font-medium">{getCourseProgress(enrollment)}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${getCourseProgress(enrollment)}%` }}
                                    ></div>
                                  </div>
                                  
                                  <div className="flex items-center justify-between text-sm">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      getCourseStatus(getCourseProgress(enrollment)).color
                                    }`}>
                                      {getCourseStatus(getCourseProgress(enrollment)).text}
                                    </span>
                                    <span className="text-gray-500">
                                      등록일: {enrollment.enrolledAt ? formatDate(enrollment.enrolledAt) : '-'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FiBookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h4 className="text-lg font-medium text-gray-900 mb-2">수강 중인 강의가 없습니다</h4>
                        <p className="text-gray-500">이 사용자는 아직 어떤 강의에도 등록하지 않았습니다.</p>
                      </div>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showFranchiseModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-white rounded-lg shadow-lg border flex flex-col max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">가맹점 계정 등록</h3>
                  <p className="text-sm text-gray-500 mt-1">일반 사용자와 동일하게 로그인하며, 관리자 화면에서 가맹점으로 구분됩니다.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowFranchiseModal(false);
                    resetFranchiseForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleCreateFranchise} className="flex-1 overflow-y-auto p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">가맹점명 / 담당자명</label>
                  <input
                    type="text"
                    name="name"
                    value={franchiseForm.name}
                    onChange={handleFranchiseFormChange}
                    required
                    minLength={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="예: 메이플 영어학원 강남점"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      name="email"
                      value={franchiseForm.email}
                      onChange={handleFranchiseFormChange}
                      required
                      className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        emailCheckStatus === 'available'
                          ? 'border-green-400'
                          : emailCheckStatus === 'duplicate' || emailCheckStatus === 'invalid'
                            ? 'border-red-400'
                            : 'border-gray-300'
                      }`}
                      placeholder="login@example.com"
                    />
                    <button
                      type="button"
                      onClick={handleCheckEmail}
                      disabled={isCheckingEmail || !franchiseForm.email.trim()}
                      className="whitespace-nowrap px-4 py-2 border border-primary-600 text-sm font-medium rounded-lg text-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCheckingEmail ? '확인 중...' : '중복 확인'}
                    </button>
                  </div>
                  {emailCheckStatus === 'available' && (
                    <p className="mt-1 text-sm text-green-600">사용 가능한 이메일입니다</p>
                  )}
                  {emailCheckStatus === 'duplicate' && (
                    <p className="mt-1 text-sm text-red-600">이미 사용 중인 이메일입니다</p>
                  )}
                  {emailCheckStatus === 'invalid' && (
                    <p className="mt-1 text-sm text-red-600">유효한 이메일 형식이 아닙니다</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호</label>
                    <input
                      type="password"
                      name="password"
                      value={franchiseForm.password}
                      onChange={handleFranchiseFormChange}
                      required
                      minLength={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호 확인</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={franchiseForm.confirmPassword}
                      onChange={handleFranchiseFormChange}
                      required
                      minLength={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">전화번호</label>
                  <input
                    type="tel"
                    name="phone"
                    value={franchiseForm.phone}
                    onChange={handleFranchiseFormChange}
                    required
                    pattern="^01[0-9]{1}-?[0-9]{4}-?[0-9]{4}$"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="010-1234-5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      name="address"
                      value={franchiseForm.address}
                      readOnly
                      required
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none"
                      placeholder="주소 검색 버튼을 눌러주세요"
                    />
                    <button
                      type="button"
                      onClick={handleOpenAddressSearch}
                      disabled={!postcodeReady}
                      className="whitespace-nowrap px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {postcodeReady ? '주소 검색' : '로딩 중...'}
                    </button>
                  </div>
                  <input
                    type="text"
                    name="addressDetail"
                    value={franchiseForm.addressDetail}
                    onChange={handleFranchiseFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="상세 주소 (동/호수 등)"
                  />
                  <p className="mt-1 text-xs text-gray-500">Daum 우편번호 서비스를 사용합니다</p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFranchiseModal(false);
                      resetFranchiseForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingFranchise}
                    className="flex-1 btn-primary disabled:opacity-50"
                  >
                    {isSubmittingFranchise ? '등록 중...' : '가맹점 계정 등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
